/**
 * Générateur — Il encadre la racine carrée d'un nombre positif entre deux
 * entiers consécutifs.
 * Attendu ID : 144 | Niveau : 4ème | Domaine : Nombres (desc 11, O9)
 *
 * Doc prof MUET en exercices (image « Exemples de réussite » p.12 : « Encadre
 * √7 entre deux entiers consécutifs SANS en chercher une valeur approchée »).
 * → BANQUE CRÉÉE. Tout repose sur a² < n < (a+1)², jamais sur une valeur
 * approchée. Plan de cartes validé 2026-07-14, revu 2026-09-22 :
 *
 *   Carte « encadrer » — {{a}} < √n < {{a+1}} (saisie 2 trous)
 *   Carte « entre » — Entre quels deux entiers ? (qcm_unique)
 *   Carte « raisonnement » — Pourquoi cet encadrement ? (qcm_unique)
 *   Carte « vf » — Vrai/Faux sur des encadrements (tableau_vf)
 *
 * Radicande n de 2 à 143, non carré parfait (sinon √n entier, pas d'encadrement
 * strict) : la borne 143 découle des carrés parfaits connus jusqu'à 144. Racines
 * en KaTeX (racineTex, tex) partout, phrase à trous comprise : sa ligne entière
 * est entre $ (mode mathématique global, technique de gen_205 / gen_924, revue
 * 2026-09-23).
 *
 * Revue 2026-09-22 : « entre », les trois distracteurs sont tirés dans une
 * fenêtre large (avant : toujours a−1, a+1, a+2, la clé était la deuxième paire
 * dans l'ordre croissant) ; « raisonnement », les distracteurs prennent la forme
 * de la clé avec une opération fausse (avant, la clé était la seule justification
 * à porter une inégalité et un carré), le distracteur « n est proche de a × (a+1) »
 * disparaît (incohérent pour n = a × (a+1)), et le radicande part de 7 pour que
 * les trois distracteurs soient faux par construction ; « vf », les cinq
 * radicandes sont distincts et le tableau porte toujours au moins une ligne vraie
 * et une fausse ; parenthèses explicatives devenues des phrases. Aucune carte
 * supprimée.
 *
 * Revue 2026-09-23 : typographie. La phrase à trous de « encadrer », laissée
 * la veille en √ brut, passe en mode mathématique global (ligne entière entre
 * $, trous en tête et en fin de ligne comme dans gen_1029) ; l'inégalité de
 * sa consigne (… < √n < …) passe en KaTeX aussi. Les cinq justifications du
 * QCM « raisonnement » (« car 4² = 16 < 20 < 25 = 5² ») sont en KaTeX, carrés
 * et inégalités compris. Le `$\sqrt{\;}$` écrit à la main de « vf » passe par
 * tex (chaîne identique). Les réponses restent du texte brut.
 *
 * Règle prof 2026-09-23 (composition des tableaux vrai-faux) : quatre lignes
 * au maximum, sinon c'est trop long à lire. « vf » passe de cinq lignes à
 * quatre : trois encadrements complets (le cœur de la carte, avec la garantie
 * d'au moins une ligne vraie et une fausse) et une seule comparaison
 * « √n < entier » au lieu de deux ; quatre radicandes distincts.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeVraiFaux, racineTex, tex,
} from '../../../utils';

const ATTENDU_ID = 144;

const L_ENCADRER = 'Encadrer une racine carrée entre deux entiers';
const L_ENTRE = 'Situer une racine carrée entre deux entiers';
const L_RAISONNEMENT = 'Justifier l\'encadrement d\'une racine carrée';
const L_VF = 'Vérifier un encadrement de racine carrée';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Partie entière EXACTE de √n (entier a tel que a² ≤ n < (a+1)²).
const isqrt = (n) => {
    let a = Math.floor(Math.sqrt(n));
    while ((a + 1) * (a + 1) <= n) a++;
    while (a * a > n) a--;
    return a;
};
const estCarre = (n) => { const r = isqrt(n); return r * r === n; };

// Tire un entier n ∈ [min;max] NON carré parfait et hors de `exclus`, avec sa
// partie entière a. Repli déterministe : le plus petit admissible.
const tireNonCarre = (min, max, exclus = null) => {
    const admissible = (n) => !estCarre(n) && !(exclus && exclus.has(n));
    for (let i = 0; i < 80; i++) {
        const n = randInt(min, max);
        if (admissible(n)) return { n, a: isqrt(n) };
    }
    for (let n = min; n <= max; n++) if (admissible(n)) return { n, a: isqrt(n) };
    return { n: 30, a: 5 };
};

// ═════════ Carte « encadrer » — {{a}} < √n < {{a+1}} (saisie) ═════════

const genEncadrerTrous = () => {
    const { n, a } = tireNonCarre(2, 143);
    // Ligne entière en mode mathématique (hasMathLines) : les deux trous sont
    // dans la formule, la racine garde sa barre.
    const ligne = `{{${a}}} \\lt \\sqrt{${n}} \\lt {{${a + 1}}}`;
    const phrase = `$${ligne}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut encadrer ${racineTex(n)}, la racine carrée de ${n}, entre deux entiers consécutifs, `
        + '<b>sans</b> chercher de valeur approchée.</p>'
        + `<p><b>Complète les deux entiers</b> : ${tex(`\\ldots \\lt \\sqrt{${n}} \\lt \\ldots`)}.</p>`,
        { ...opts(L_ENCADRER), mode: 'saisie' });
    payload._meta.params = { n, a };
    return payload;
};

// ═════════ Carte « entre » — Entre quels deux entiers ? (QCM) ═════════

const genEntreQcm = () => {
    const { n, a } = tireNonCarre(2, 143);
    const correct = `${a} et ${a + 1}`;
    // Fenêtre large [a−3 ; a+3] hors a, bornes strictement positives : trois
    // paires tirées au hasard, le rang de la clé dans l'ordre croissant varie.
    const cand = [];
    for (let x = a - 3; x <= a + 3; x++) if (x >= 1 && x !== a) cand.push(`${x} et ${x + 1}`);
    const choix = [correct, ...shuffle(cand).slice(0, 3)];
    const payload = makeQcm(choix, correct,
        `<p><b>Entre quels deux entiers consécutifs se trouve ${racineTex(n)} ?</b></p>`, opts(L_ENTRE));
    payload._meta.params = { n, a };
    return payload;
};

// ═════════ Carte « raisonnement » — Pourquoi cet encadrement ? (QCM) ═════════

// Distracteurs de la MÊME forme que la clé « car a² = … < n < … = (a+1)² »,
// avec une opération fausse. Chacun est faux par construction pour n ≥ 7 :
//   · double : a × 2 = 2a < n < 2a + 2 = (a+1) × 2 (ou la somme a + a), le carré
//     confondu avec le double ; n dans ]2a ; 2a + 2[ n'arrive que pour n = 5 ;
//   · carrés décalés : (a−1)² < n < a², faux puisque n > a² ;
//   · somme des bornes : a + (a+1) = 2a + 1 < n < 2a + 3 = (a+1) + (a+2) ;
//     n dans ]2a + 1 ; 2a + 3[ n'arrive que pour n = 6.
// Les trois portent des valeurs distinctes entre elles et distinctes de la clé.
// Chaque justification : « car » puis la chaîne d'inégalités en KaTeX.
const genRaisonnementQcm = () => {
    const { n, a } = tireNonCarre(7, 143);
    const bas = a * a; const haut = (a + 1) * (a + 1);
    const correct = `car ${tex(`${a}^2 = ${bas} \\lt ${n} \\lt ${haut} = ${a + 1}^2`)}`;
    const double = randomChoice([
        `car ${tex(`${a} \\times 2 = ${2 * a} \\lt ${n} \\lt ${2 * a + 2} = ${a + 1} \\times 2`)}`,
        `car ${tex(`${a} + ${a} = ${2 * a} \\lt ${n} \\lt ${2 * a + 2} = ${a + 1} + ${a + 1}`)}`,
    ]);
    const faux = [
        double,
        `car ${tex(`${a - 1}^2 = ${(a - 1) * (a - 1)} \\lt ${n} \\lt ${bas} = ${a}^2`)}`,
        `car ${tex(`${a} + ${a + 1} = ${2 * a + 1} \\lt ${n} \\lt ${2 * a + 3} = ${a + 1} + ${a + 2}`)}`,
    ];
    const choix = [correct, ...shuffle(faux)];
    const payload = makeQcm(choix, correct,
        `<p>On sait que ${tex(`${a} \\lt \\sqrt{${n}} \\lt ${a + 1}`)}.</p>`
        + '<p><b>Quelle justification est correcte ?</b></p>', opts(L_RAISONNEMENT));
    payload._meta.params = { n, a, distracteurs: ['double', 'carres_decales', 'somme_bornes'] };
    return payload;
};

// ═════════ Carte « vf » — Vrai/Faux sur des encadrements (tableau) ═════════

const genVfTableau = () => {
    const lignes = [];
    const bonnes = [];
    const pousser = (txt, vrai) => { lignes.push(txt); bonnes.push(vrai ? 0 : 1); };
    const vus = new Set();                             // radicandes déjà tirés : quatre lignes, quatre radicandes

    // Quatre lignes (règle prof 2026-09-23) : trois encadrements et une comparaison.
    // 3 encadrements complets a < √n < a+1 : au moins un vrai et un faux par
    // construction, le troisième au hasard.
    const verites = shuffle([true, false, randomChoice([true, false])]);
    for (let i = 0; i < 3; i++) {
        const { n, a } = tireNonCarre(2, 143, vus);
        vus.add(n);
        let bg = a;
        if (!verites[i]) bg = a - 1 >= 1 ? randomChoice([a - 1, a + 1]) : a + 1;
        pousser(tex(`${bg} \\lt \\sqrt{${n}} \\lt ${bg + 1}`), bg === a);
    }
    // 1 comparaison simple √n < entier (vrai ssi n < entier²).
    const { n: nc, a: ac } = tireNonCarre(2, 143, vus);
    vus.add(nc);
    const bc = randomChoice([ac, ac + 1]);         // √n < a est faux, √n < a+1 est vrai
    pousser(tex(`\\sqrt{${nc}} \\lt ${bc}`), nc < bc * bc);
    const ordre = shuffle(lignes.map((_, i) => i));
    const L = ordre.map(i => lignes[i]);
    const B = ordre.map(i => bonnes[i]);
    const payload = makeVraiFaux(L, B,
        `<p>Le symbole ${tex('\\sqrt{\\;}')} désigne la racine carrée.</p><p><b>Vrai ou faux ?</b></p>`, opts(L_VF));
    payload._meta.params = { lignes: L, bonnes: B };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    encadrer_texte_trou_saisie: genEncadrerTrous,
    entre_qcm_unique: genEntreQcm,
    raisonnement_qcm_unique: genRaisonnementQcm,
    vf_tableau_vf: genVfTableau,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'encadrer', label: L_ENCADRER, formatsList: buildFormats('encadrer') },
    { id: 'entre', label: L_ENTRE, formatsList: buildFormats('entre') },
    { id: 'raisonnement', label: L_RAISONNEMENT, formatsList: buildFormats('raisonnement') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
